import { nanoid } from "nanoid";
import React, { ReactNode, Suspense, useContext, useEffect, useMemo, useRef } from "react";
import { RevUserContext, RevUserContextType } from "../../context/UserContext";
import { FavoriteApi } from "../../redux/api/FavoriteApi";
import { FeedApi } from "../../redux/api/FeedApi";
import { useReduxDispatch, useReduxSelector } from "../../redux/hooks";
import { ActivityFeed, ToolProps } from "./ActivityFeed/ActivityFeed";
import { Contacts } from "./Contacts/Contacts";
import { Revelations } from "./Revelations/Revelations";
import "./RightBar.scss";
import { ToolButton } from "./ToolButton";
import { IContact } from "../../types/User/IContact";
import { updateToggleState, setDockOrder, setSelectedId } from "../../slices/RightBarSlice";
import { ChatBar } from "./Chat/ChatBar";
import ChatRequest from "../Chat/ChatRequest";
import { ErrorBoundary } from "../Common/ErrorBoundry";
import { useParams } from "react-router-dom";
import { UserApi } from "../../redux/api/UserApi";
import { Links } from "./Links/Links";
import { Notifications } from "./Notifications/Notifications";
import { MessageApi } from "../../redux/api/MessageApi";
import { Setting } from "../../types/Setting/Setting";
import { Button } from "@progress/kendo-react-buttons";
import * as svg from "@progress/kendo-svg-icons";
import { Sortable, SortableItemUIProps, SortableOnDragOverEvent } from "@progress/kendo-react-sortable";

//@ts-expect-error
const ChatCount = React.lazy(() => import("chat/ChatCount").catch());
//@ts-expect-error
const PinnedChatModule = React.lazy(() => import("chat/PinnedChats").catch());
//@ts-expect-error
const ChatNotificationModule = React.lazy(() => import("chat/ChatNotification").catch());

export enum ToggleState {
  Uninitialized = 0,
  Collapsed = 1,
  Expanded = 2,
  Free = 3,
}

export const RightBar = ({ children }: { children: ReactNode }) => {
  const { profile } = React.useContext<RevUserContextType>(RevUserContext)
  const { toggleState: locked, dockOrder: newDockOrder, selectedId } = useReduxSelector((state) => state.rightBar);
  //const { FeedItems, LastId } = useReduxSelector(rev => rev.feed.feedItems)
  const [lastId, setLastId] = React.useState<number>();
  //const [isLocked, setIsLocked] = React.useState<number>(locked);
  const dispatch = useReduxDispatch();
  //const [newDockOrder, setNewDockOrder] = React.useState<string[]>();
  const { data: feedResult } = FeedApi.useGetUserFeedQuery({ uid: profile.Uid, lastId: lastId ? lastId.toString() : '' }, {
    pollingInterval: 20000,
    skip: !profile?.Features.Activity_Feed,
  });
  //Use new setting (id=139) to determine the order of the tools in the right bar.
  // const { data: config } = FavoriteApi.useGetFavoriteConfigQuery(profile.Uid, { skip: profile?.Features.External_Interface });
  const { data: rightBarSettings } = UserApi.useGetSettingQuery(Setting.RIGHTBAR_STATE)
  const [saveDockSetting] = UserApi.useSaveSettingMutation()
  const [saveFavConfig] = FavoriteApi.useSaveFavoriteConfigMutation();
  const [selectedContact, setSelectedContact] = React.useState<IContact>();
  const { data: notificationConfig } = MessageApi.useGetConfigQuery()
  const [editMode, setEditMode] = React.useState(false);
  //Show the current contact from the ticket details page.
  const { contactId: ticketContactId } = useParams()
  const { data: ticketContact } = UserApi.useGetUserByIdQuery(parseInt(ticketContactId), { skip: !ticketContactId })
  const { data: messages } = MessageApi.useGetMessagesQuery()
  let unReadCount = messages?.filter(m => !m.Is_Read).length || 0
  useEffect(() => {
    if (ticketContact) {
      //Expand contacts
      dispatch(setSelectedId("contacts"));
      setSelectedContact({
        ID: ticketContact.ID,
        FullName: ticketContact.FullName,
      } as IContact);

      //console.log('ticketContact', locked, ticketContact)
      if (locked)
        dispatch(updateToggleState(ToggleState.Free))
    }
  }, [ticketContact])


  //Update the feed items in reduc state
  useEffect(() => {
    //console.log('RightBar.tsx', feedResult)
    //if (feedResult tId)
    //dispatch(FeedSlice.SetFeedItems(feedResult));
    if (feedResult && feedResult.LastId > 0 && feedResult.LastId !== lastId)
      setLastId(feedResult?.LastId)
  }, [feedResult, dispatch]);

  //load the tools order from the settings and whether the tools are docked or not.
  useEffect(() => {
    if (rightBarSettings && !newDockOrder?.length) {
      console.warn('RightBar.tsx - rightBarSettings', rightBarSettings)
      var dockOrder = rightBarSettings?.String_Value?.split(',');
      dispatch(setDockOrder(dockOrder));
      if (dockOrder.length > rightBarSettings.Int_Value)
        dispatch(setSelectedId(dockOrder[rightBarSettings.Int_Value]));

      //Expand or collapse the tools based on the settings
      if (rightBarSettings?.Bool_Value !== (locked === ToggleState.Expanded)) {
        dispatch(updateToggleState(rightBarSettings?.Bool_Value ? ToggleState.Expanded : ToggleState.Collapsed));
      }
    }
  }, [rightBarSettings]);




  ///Listen for contact name click from CallHistory.aspx page in the IFrame
  useEffect(() => {
    //Add event listener for Events that get posted as messages in the IFrame so we can trigger things like updating the banner
    const messageListener = (e) => {
      try {
        if (e.data?.indexOf && e.data?.indexOf("{") === 0) {
          var messageObj = JSON.parse(e.data);
          //console.log('messageObj', messageObj)
          if ("message" in messageObj && messageObj.message === "ShowContact") {
            if (
              "contactId" in messageObj &&
              messageObj.contactId &&
              !isNaN(messageObj.contactId)
            ) {
              console.log("setting expanded", locked);
              //Expand contacts
              setSelectedId("contacts");

              setSelectedContact({
                ID: messageObj.contactId,
                FullName: messageObj.contactName,
              } as IContact);
              if (locked === ToggleState.Collapsed) {
                dispatch(updateToggleState(ToggleState.Free));
              }
              //setFavOnly(false);
              //setContactIdSelected(messageObj.contactId)
              //setFilter(messageObj.contactName)
            }
          }
        }
      } finally {
      }
    };

    var ev = window.addEventListener("message", messageListener, false);
    return () => {
      window.removeEventListener("message", messageListener);
    };
  }, [locked, dispatch]);

  const buttonClicked = async (id) => {
    console.log('buttonClicked', id, locked, selectedId)
    if (locked === ToggleState.Collapsed || locked === ToggleState.Uninitialized) {
      // let newConfig = { ...config, Page_State_Xml: '<DictionaryOfStringBoolean><item key="0" value="True" /></DictionaryOfStringBoolean>' }
      // await saveFavConfig(newConfig);

      dispatch(updateToggleState(ToggleState.Free));
    } else if (locked === ToggleState.Free && selectedId === id)
      dispatch(updateToggleState(ToggleState.Collapsed));

    if (selectedId !== id) {
      dispatch(setSelectedId(id));
    }

    await saveDockSetting({ ...rightBarSettings, Int_Value: newDockOrder.indexOf(id), Date_Value: new Date().toISOString() });

    // if (selectedId !== id) {
    //   //let newOrder = tools.map(t => t.id);
    //   //console.log('dockOrder', dockOrder)
    //   let newOrder = tools.map(t => t.id)
    //   newOrder.splice(newOrder.indexOf(id), 1);
    //   newOrder.unshift(id);
    //   console.log('newOrder', newOrder)
    //   //Update the order via the API
    //   let newOrderXML = `<DictionaryOfInt32String><item key="0" value="${newOrder[0]}" /><item key="1" value="${newOrder[1]}" /><item key="2" value="${newOrder[2]}" /><item key="3" value="${newOrder[3]}" /></DictionaryOfInt32String>`;

    //   setSelectedId(id);
    //   let newConfig = { ...config, Dock_Order: newOrderXML };
    //   await saveFavConfig({ config: newConfig, uid: profile.Uid });
    // }
  };

  const unlockTools = async () => {

    dispatch(updateToggleState(ToggleState.Collapsed));
    //setIsLocked(ToggleState.Collapsed);

    //Save new Setting
    if (rightBarSettings) {
      await saveDockSetting({ ...rightBarSettings, Bool_Value: false, Date_Value: new Date().toISOString() });
    }
  };
  let lockingTools = false;
  const lockTools = async () => {
    lockingTools = true;
    console.log("lockTools");
    dispatch(updateToggleState(ToggleState.Expanded));
    //setIsLocked(ToggleState.Expanded);

    // let newConfig = {
    //   ...config,
    //   Page_State_Xml:
    //     '<DictionaryOfStringBoolean><item key="0" value="True" /></DictionaryOfStringBoolean>',
    // };
    // await saveFavConfig({ config: newConfig, uid: profile.Uid });

    //Save new Setting
    if (rightBarSettings) {
      await saveDockSetting({ ...rightBarSettings, Bool_Value: true, Date_Value: new Date().toISOString() });
    }
  };


  let leaveTimeout = null;
  const handleMouseLeave = (e) => {
    //e.stopPropagation();
    //e.preventDefault();
    console.log('handleMouseLeave locked=', locked, lockingTools)
    if (locked === ToggleState.Free && !lockingTools) {
      console.log('handleMouseLeave locked=', locked, lockingTools, leaveTimeout)
      if (leaveTimeout) window.clearTimeout(leaveTimeout);

      leaveTimeout = window.setTimeout(() => {
        dispatch(updateToggleState(ToggleState.Collapsed));
      }, 100);
      //setIsLocked(ToggleState.Collapsed)
    }
  };

  const handleClick = (e) => {
    e && e.stopPropagation();
    e && e.preventDefault();

    if (locked === ToggleState.Free) {
      console.log('handleClick locked=', locked)
      dispatch(updateToggleState(ToggleState.Collapsed));
      //setIsLocked(ToggleState.Collapsed)
    }
  };

  const toolProps: ToolProps = {
    fullScreen: false,
    unlocked: unlockTools,
    locked: lockTools,
    itemSelected: handleClick,
    onMouseLeave: handleMouseLeave
  };
  const tools = useMemo(
    () =>
      [
        {
          id: "chat",
          imageUrl: "./images/Chat.png",
          class: "chat-button",
          element: <ChatBar {...toolProps} itemSelected={handleClick} />,
          title: "Chat",
          badge:
            <ErrorBoundary errorMessage="?">
              <ChatCount />
            </ErrorBoundary>,
          visible: profile?.Features.Chat,
          order: newDockOrder?.indexOf("chat"),
        },
        {
          id: "activity",
          imageUrl: "./images/Activity-Feed-Dark.png",
          class: "feed-button",
          element: <ActivityFeed {...toolProps} feedResults={feedResult} />,
          title: "Activity feed",
          badge:
            feedResult?.UnReadCount > 0
              ? feedResult?.UnReadCount.toString()
              : null,
          visible: profile?.Features.Activity_Feed,
          order: newDockOrder?.indexOf("activity"),
        },
        {
          id: "contacts",
          imageUrl: "./images/Contacts-Dark.png",
          class: "contacts-button",
          element: (
            <Contacts
              {...toolProps}
              selectedContact={selectedContact}
            />
          ),
          title: "Contacts",
          badge: null,
          visible: profile?.Features.Contacts_Dock,
          order: newDockOrder?.indexOf("contacts"),
        },
        {
          id: "favorites",
          imageUrl: "./images/Revelations-Dark.png",
          class: "revelations-button",
          element: <Revelations {...toolProps} />,
          title: "Revelations",
          badge: null,
          visible: profile?.Features.Revelations,
          order: newDockOrder?.indexOf("favorites"),
        },
        {
          id: "links",
          imageUrl: "./images/Link-Out.png",
          class: "links-button",
          element: <Links {...toolProps} />,
          title: "Links",
          badge: null,
          visible: profile?.Features.Link_Out,
          order: newDockOrder?.indexOf("links"),
        },
        {
          id: "notifications",
          imageUrl: "./images/dark-bell-icon.png",
          class: "notifications-button",
          element: <Notifications {...toolProps} />,
          title: "Notifications",
          badge: unReadCount > 0
            ? unReadCount.toString()
            : null,
          visible: notificationConfig?.View_Message_Feed,
          order: newDockOrder?.indexOf("notifications"),
        },
      ].sort((x, y) => x.order - y.order),
    [feedResult, profile, newDockOrder, selectedContact, dispatch, notificationConfig, unReadCount]
  );

  const SortableItemUI1 = (props) => {
    return (
      <div className="sortable-item" {...props} style={{
        borderStyle: 'dashed', borderWidth: '1px', borderColor: 'black', padding: '2px', margin: '5px'
      }}>
        <ToolButton {...props.dataItem as ITool} onClick={null} selected={false} />
        {/* <Button>
          <img src={props.dataItem.imageUrl} alt={props.dataItem.title} />
        </Button> */}
      </div>
    );
  };
  const baseItemStyle = {
    height: 50,
    lineHeight: '48px',
    fontSize: '13px',
    textAlign: 'left',
    outline: 'none',
    border: '1px dashed',
    cursor: 'move',
    display: 'block',
    paddingLeft: 6,
    paddingRight: 6,
    margin: 2,
    borderColor: '#000',
    width: 'auto',
    userSelect: 'none',
  } as React.CSSProperties;


  const SortableItemUI = (props: SortableItemUIProps) => {
    const { isActive, style, attributes, dataItem, forwardRef } = props;
    console.log('SortableItemUI', props.isDragCue)
    if (props.isDragCue) return null;
    return (
      <div ref={forwardRef}
        {...attributes}
        style={{
          ...baseItemStyle
        }}
      >
        <div className='sortable-item' title={dataItem.Title}>
          <img height={30} src={(dataItem as ITool).imageUrl} />{(dataItem as ITool).id}
        </div>
      </div>
    );
  };

  const onDragOver = (event: SortableOnDragOverEvent) => {
    console.log('onDragOver', event.newState)
    dispatch(setDockOrder((event.newState as Array<ITool>).map(t => t.id)));
  };

  const saveDockOrder = async () => {
    //Save new Setting
    if (rightBarSettings) {
      await saveDockSetting({ ...rightBarSettings, String_Value: newDockOrder.join(','), Date_Value: new Date().toISOString() });
    }
    setEditMode(false);
  }


  const toolsClass = `right-bar ${tools.filter((t) => t.visible).length === 1 ? "one-tool" : ""
    }`;
  const hasTools = tools.find((t) => t.visible)

  //Log to the console why this react component rerenders
  function useTraceUpdate(props) {
    const prev = useRef(props);
    useEffect(() => {
      const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
        if (prev.current[k] !== v) {
          ps[k] = [prev.current[k], v];
        }
        return ps;
      }, {});
      if (Object.keys(changedProps).length > 0) {
        console.warn('RIGHTBAR.tsx Changed props:', changedProps);
      }
      prev.current = props;
    });
  }


  //SSI
  if (profile?.Features.External_Interface && !hasTools)
    return (
      <section
        className={toolsClass}
        style={{ backgroundColor: profile.Body_Hex_Color }}>
        <main>{children}
        </main>
        <Suspense fallback={<></>}>
          <ChatRequest />
        </Suspense>
      </section>)

  //Support staff
  return (
    <section
      className={toolsClass}
    // style={{ backgroundColor: halfNavcolor }}
    >
      {tools.find((t) => t.visible) && (
        <div
          className={
            locked === ToggleState.Expanded
              ? "tools"
              : locked === ToggleState.Free
                ? "tools-free"
                : "tools-hidden"
          }
        >
          <div className="menu">
            {editMode ? <>
              <Sortable idField={'id'} data={tools.filter(t => t.visible)} itemUI={SortableItemUI} onDragOver={onDragOver}></Sortable>
            </>
              : <>{tools
                .filter((t) => t.visible)
                .map((t, i) => {
                  return (
                    <ToolButton
                      {...t}
                      onClick={buttonClicked}
                      selected={selectedId === t.id || (!selectedId && i === 0)}
                      key={nanoid()}
                    />
                  );
                })}</>

            }
            {editMode ? <div className="d-flex">
              <Button svgIcon={svg.saveIcon} fillMode="solid" title="Save" onClick={() => saveDockOrder()}></Button>
              <Button svgIcon={svg.cancelIcon} fillMode="outline" title="Cancel" onClick={() => setEditMode(false)}></Button>
            </ div>
              : <>{(locked === ToggleState.Collapsed || locked === ToggleState.Uninitialized) && tools.filter((t) => t.visible).length > 1 && <Button svgIcon={svg.moreVerticalIcon} fillMode="flat" title="Reorder" onClick={() => setEditMode(true)}></Button>}</>
            }
          </div>
          <ErrorBoundary>
            <Suspense fallback={''}>
              <ChatNotificationModule pinChatOnClick={true} />
              <PinnedChatModule />
            </Suspense>
          </ErrorBoundary>
          {locked !== ToggleState.Collapsed && locked !== ToggleState.Uninitialized &&
            tools.find((t) => t.visible && (!selectedId || selectedId === t.id))?.element}
        </div>
      )}
      <main>{children}</main>
      {/* dockOrder={JSON.stringify(newDockOrder)} */}
    </section>
  );
};

type ITool = {
  id: string;
  imageUrl: string;
  class: string;
  element: ReactNode;
  title: string;
  badge: string | null;
  visible: boolean;
  order: number;
};